/**
 * Permisos v8 del operador (roles de sporttia-manager). Es la misma lógica que
 * `OperatorPermissions` del manager nuevo, para que los dos gestores decidan
 * igual qué puede hacer un operador.
 */

const crud = <M extends string>(module: M) =>
    ({
        VIEW: `${module}.view`,
        CREATE: `${module}.create`,
        MODIFY: `${module}.modify`,
        DELETE: `${module}.delete`,
    }) as const;

/**
 * Catálogo de permisos del API (`GET /v8/manager/permissions`) como
 * constantes: `PERMISSIONS.BOOKING.CREATE` en vez de `'booking.create'`, para
 * que un código mal escrito sea un error de tipos y no un botón que desaparece
 * sin avisar.
 */
export const PERMISSIONS = {
    CUSTOMER: crud('customer'),
    GROUP: crud('group'),
    VOUCHER: crud('voucher'),
    FACILITY: crud('facility'),
    RATE: crud('rate'),
    DISCOUNT: crud('discount'),
    SCHEDULE: crud('schedule'),
    CLOSURE: crud('closure'),
    BOOKING: crud('booking'),
    FIXED_BOOKING: crud('fixed_booking'),
    RESTRICTION: crud('restriction'),
    ACTIVITY: crud('activity'),
    EVENT: crud('event'),
    COMPETITION: crud('competition'),
    DEVICE: crud('device'),
    SALE_POINT: crud('sale_point'),
    ACCOUNTING: crud('accounting'),
    REMITTANCE: crud('remittance'),
    REPORT: { VIEW: 'report.view' },
    COMMUNICATION: crud('communication'),
    CONFIGURATION: {
        VIEW: 'configuration.view',
        MODIFY: 'configuration.modify',
    },
    ADMINISTRATION: crud('administration'),
} as const;

type PermissionModules = typeof PERMISSIONS;

/** Cualquier código del catálogo, por ejemplo `'booking.create'`. */
export type PermissionCode = {
    [M in keyof PermissionModules]: PermissionModules[M][keyof PermissionModules[M]];
}[keyof PermissionModules];

/** Un código, o una lista de códigos que se exigen todos. */
export type PermissionRequirement = PermissionCode | readonly PermissionCode[];

/** Todos los códigos del catálogo. */
export const ALL_PERMISSION_CODES: readonly PermissionCode[] = Object.values(
    PERMISSIONS,
).flatMap((actions) => Object.values(actions) as PermissionCode[]);

/**
 * Lo que hace falta para decidir un permiso: los códigos del operador
 * (`GET /v8/manager/me`) y si los roles están encendidos (flag
 * `manager-roles-permissions`). `AppContext` cumple esta forma.
 */
export type OperatorAccess = {
    permissions: readonly string[];
    rolesEnabled: boolean;
};

const toList = (required: PermissionRequirement): readonly PermissionCode[] =>
    typeof required === 'string' ? [required] : required;

/**
 * Si el operador puede hacer algo. Con los roles apagados siempre es `true`:
 * el gestor se comporta como antes de los roles. Con los roles encendidos, un
 * código exige ese código y una lista exige todos.
 */
export function can(
    access: OperatorAccess,
    required: PermissionRequirement,
): boolean {
    if (!access.rolesEnabled) return true;

    return toList(required).every((code) => access.permissions.includes(code));
}

/** Como `can`, pero basta con uno de los códigos de la lista. */
export function canAny(
    access: OperatorAccess,
    required: readonly PermissionCode[],
): boolean {
    if (!access.rolesEnabled) return true;

    return required.some((code) => access.permissions.includes(code));
}
